import React, { useState } from 'react';
import { createStore, applyMiddleware } from 'redux';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import thunk from 'redux-thunk';
import rootReducer from './rootReducer';
import { BrowserRouter } from 'react-router-dom';
import { Switch, Route, Redirect } from 'react-router';
import translations from './i18n/translations';
import { IntlProvider } from 'react-intl';
import './App.scss';
import { shouldPolyfill } from '@formatjs/intl-pluralrules/should-polyfill';
import Navbar from './navbar/navbar';
import AuthenticatedRoute from './utils/authenticatedRoute';
import UserForm from './user/userForm';
import LoginSignup from './loginSignup/loginSignup';
import Shops from './shops/shops';
import MarketplaceAdmin from './marketplace/marketplaceAdmin';
import Landing from './landing/landing';
import Admin from './admin/admin';
import MarketplaceCreate from './marketplace/marketplaceCreate';
import OrderBatchBill from './bills/orderBatchBill';
import RoutePlanner from './routePlanner/routePlanner';

async function polyfill(locale) {
  if (shouldPolyfill()) {
    // Load the polyfill 1st BEFORE loading data
    await import('@formatjs/intl-pluralrules/polyfill');
  }

  if (Intl.PluralRules.polyfilled) {
    switch (locale) {
      default:
        await import('@formatjs/intl-pluralrules/locale-data/en');
        break;
      case 'fr':
        await import('@formatjs/intl-pluralrules/locale-data/fr');
        break;
    }
  }
}

const store = createStore(rootReducer, applyMiddleware(thunk));
const reactQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

function App() {
  polyfill();

  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'et');

  function setLang(lang) {
    localStorage.setItem('lang', lang);
    setLanguage(lang);
  }

  const messages = translations[language];

  return (
    <IntlProvider locale={'et'} messages={messages}>
      <QueryClientProvider client={reactQueryClient}>
        <BrowserRouter>
          <Provider store={store}>
            <Navbar setLanguage={setLang} />

            <div className="main">
              <Switch>
                <Route path="/login" component={LoginSignup} />
                <AuthenticatedRoute path="/edit-profile" render={(props) => <UserForm {...props} />} />
                <AuthenticatedRoute path="/user/edit/:userId" render={(props) => <UserForm {...props} />} />
                <AuthenticatedRoute path="/shops" render={(props) => <Shops {...props} />} />
                <AuthenticatedRoute path="/admin" render={(props) => <Admin {...props} />} />
                <AuthenticatedRoute path="/marketplace/create" render={(props) => <MarketplaceCreate {...props} />} />
                <AuthenticatedRoute
                  path="/marketplaceAdmin/:marketplaceId"
                  render={(props) => <MarketplaceAdmin {...props} />}
                />
                <AuthenticatedRoute path="/routePlanner" render={(props) => <RoutePlanner {...props} />} />
                <AuthenticatedRoute
                  path="/orderBatchBill/:orderBatchId"
                  render={(props) => <OrderBatchBill {...props} />}
                />
                <Route path="/" component={Landing} />
                <Redirect to="/" />
              </Switch>
            </div>
          </Provider>
        </BrowserRouter>
      </QueryClientProvider>
    </IntlProvider>
  );
}

export default App;
